import { useMemo, useState } from "react";
import { format, formatDistanceToNow } from "date-fns";
import {
  AlertTriangle,
  Clock,
  History,
  Loader2,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useSyncState, useUpdateSyncState } from "@/hooks/use-itsm-config";
import type { SyncState } from "@/hooks/use-itsm-config";

interface Props {
  tenantId: string;
  /** integrations-api credential id — same value the collector keys on. */
  credentialId: string;
}

// Collector caps each incremental fetch to sync_max_catchup_minutes
// (10080 min = 7 days). A cursor older than this is silently clamped by
// the sync, so we clamp/warn here too.
const MAX_CATCHUP_DAYS = 7;

type PresetKey = "24h" | "7d";

const PRESETS: { key: PresetKey; label: string; days: number }[] = [
  { key: "24h", label: "Last 24 hours", days: 1 },
  { key: "7d", label: "Last 7 days", days: 7 },
];

const STATUS_VARIANT: Record<
  SyncState["status"],
  "positive" | "destructive" | "warning" | "secondary"
> = {
  active: "positive",
  failed: "destructive",
  paused: "warning",
  inactive: "secondary",
};

/** Format a Date as the `datetime-local` input value (local time, no tz). */
function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/**
 * Ongoing sync state (the "cursor"). Shows how far the scheduled sync has
 * progressed (last_sync_at) and lets an admin move it back to re-pull
 * recent tickets. Older-than-7-days needs the historic ITSM import instead.
 */
export function SyncStateSection({ tenantId, credentialId }: Props) {
  const { data: state, isLoading } = useSyncState(tenantId, credentialId);
  const updateSyncState = useUpdateSyncState(tenantId, credentialId);

  const earliest = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - MAX_CATCHUP_DAYS);
    return d;
  }, []);

  const [value, setValue] = useState<string>("");

  const chosen = value ? new Date(value) : null;
  const isTooOld = chosen != null && chosen < earliest;
  const activePreset = useMemo(() => {
    if (!value) return null;
    return PRESETS.find((p) => value === toLocalInputValue(daysAgo(p.days)))
      ?.key;
  }, [value]);

  const applyPreset = (days: number) => {
    setValue(toLocalInputValue(daysAgo(days)));
  };

  const handleSave = async () => {
    if (!chosen || Number.isNaN(chosen.getTime())) {
      toast.error("Pick a valid date and time first");
      return;
    }
    // Clamp to the 7-day catch-up floor; the sync would clamp anyway.
    const effective = chosen < earliest ? earliest : chosen;
    try {
      await updateSyncState.mutateAsync(effective.toISOString());
      toast.success("Sync cursor moved");
      setValue("");
    } catch {
      toast.error("Failed to move sync cursor");
    }
  };

  const lastSync = state?.last_sync_at ? new Date(state.last_sync_at) : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-4 w-4 text-muted-foreground" />
          Ongoing sync cursor
        </CardTitle>
        <CardDescription>
          The scheduled sync runs every 5 minutes (at :00, :05, :10, …) and
          pulls tickets updated since the cursor below. Move it back to re-pull
          recent tickets on the next run.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex h-24 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !state ? (
          <div className="flex items-start gap-3 rounded-md border border-dashed p-4">
            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              This integration hasn't synced yet — no cursor exists. It will be
              created on the first scheduled sync.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Current-cursor hero */}
            <div className="rounded-lg border bg-muted/40 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Last synced up to
                  </p>
                  <p className="text-2xl font-semibold tabular-nums">
                    {lastSync
                      ? format(lastSync, "PPp")
                      : "— initial 30-day window"}
                  </p>
                  {lastSync && (
                    <p className="text-sm text-muted-foreground">
                      {formatDistanceToNow(lastSync, { addSuffix: true })}
                    </p>
                  )}
                </div>
                <Badge
                  variant={STATUS_VARIANT[state.status]}
                  className="capitalize"
                >
                  {state.status}
                </Badge>
              </div>

              {state.consecutive_failures > 0 && (
                <div className="mt-3 flex items-center gap-2 text-sm text-destructive">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>
                    {state.consecutive_failures} consecutive failure
                    {state.consecutive_failures === 1 ? "" : "s"}
                    {state.error_message ? ` — ${state.error_message}` : ""}
                  </span>
                </div>
              )}
            </div>

            <Separator />

            {/* Move-cursor control */}
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="sync-cursor-input">Move cursor back to</Label>
                <p className="text-sm text-muted-foreground">
                  Pick a quick preset or a specific date and time.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {PRESETS.map((p) => (
                  <Button
                    key={p.key}
                    type="button"
                    variant={activePreset === p.key ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      applyPreset(p.days);
                    }}
                  >
                    {p.label}
                  </Button>
                ))}
                <Input
                  id="sync-cursor-input"
                  type="datetime-local"
                  className="w-[240px]"
                  value={value}
                  min={toLocalInputValue(earliest)}
                  max={toLocalInputValue(new Date())}
                  onChange={(e) => {
                    setValue(e.target.value);
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  disabled={!chosen || updateSyncState.isPending}
                  onClick={() => {
                    void handleSave();
                  }}
                >
                  {updateSyncState.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCcw className="mr-2 h-4 w-4" />
                  )}
                  Move cursor
                </Button>
              </div>

              {isTooOld && (
                <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <p className="text-sm">
                    The scheduled sync only looks back {MAX_CATCHUP_DAYS} days,
                    so this will be clamped to {format(earliest, "PPp")}. To
                    bring in older tickets, use the historic ITSM import
                    instead.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** A Date `days` before now (local). */
function daysAgo(days: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d;
}
